/**
 * 공속 계산 엔진 — 둥봇(ryhlab.com/speed)의 셈법을 그대로 따른다.
 *
 * 화면과 떼어 둔 순수 함수들이다. 숫자가 어긋나면 여기서 잡아야지, 화면에서 잡으면 안 된다.
 *
 * **속도**
 *   최종속 = 올림( 기본속 × (1 + (리더% + 토템%) ÷ 100) + 룬속 )
 *   토템은 길드 속도 토템 15% 로 고정이다(둥봇도 그렇다). 정보창의 "108 +42" 에서
 *   108 이 기본속, +42 가 룬속이다.
 *
 * **선턴 몹이 치는 틱**
 *   틱마다 공격바가 `0.07 × 최종속` 만큼 찬다. 100 을 처음 넘기는 틱이
 *   d = 내림(100 ÷ (0.07 × 최종속)) + 1 이다.
 *
 * **뒤 몹(l번째, 몬2=1, 몬3=2)이 제 차례(d + l 틱)에 치려면**
 *   그 틱에 공격바가 선턴 몹이 그때까지 쌓았을 양(0.07 × 선턴속 × (d + l)) 이상이어야 한다.
 *   선턴 몹이 뿌린 게증(p)은 그대로 더해지고, 속업(o)은 선턴이 친 뒤의 l 틱에만 붙는다.
 *     0.07 × S × (d + l × (1 + o)) + p  ≥  0.07 × 선턴속 × (d + l)
 *   → 필요 최종속 S = (0.07 × 선턴속 × (d + l) − p) ÷ (0.07 × (d + l × (1 + o)))
 *   속업 o = 30% × (1 + 공속아티% ÷ 100). 아티는 속업 효과를 키우는 값이다.
 *
 * **차례 지키기**
 *   앞 자리는 뒷 자리보다 빨라야 한다. 필요 룬속을 뒤에서부터 거슬러 올라가며, 앞 몹의
 *   최종속이 뒷 몹보다 작지 않게 끌어올린다.
 */

export const TOTEM_PCT = 15;
export const ATB_PER_SPEED = 0.07;
export const SPEED_BUFF_PCT = 30;

export type Mode = 'gauge' | 'buff' | 'both';

export const MODE_LABEL: Record<Mode, string> = {
    gauge: '게증만',
    buff: '속업만',
    both: '게증+속업'
};

export interface Mon {
    /** 몹 이름(화면용) */
    name: string;
    /** 몹 고유 기본속 */
    base: number;
    /** 룬으로 오른 속도 (정보창의 +N) */
    flat: number;
    /** 공속아티 % — 속업 효과를 키운다 (뒤 몹에만 의미가 있다) */
    artiPct?: number;
    /**
     * 룬 밖에서 붙는 속도 — 물호박(걸린 버프 수만큼)·암헬하(공속 +N) 같은 패시브.
     * 룬속처럼 그대로 더해진다(둥봇의 bonusOptions). 뒤 몹이면 필요 룬속에서 그만큼 빠진다.
     */
    bonus?: number;
}

export interface Options {
    leaderPct: number;
    /** 선턴 몹이 뿌리는 게이지 증가 % (게증만·게증+속업일 때) */
    gaugePct: number;
    mode: Mode;
    totemPct?: number;
}

export interface FollowerResult {
    name: string;
    base: number;
    flat: number;
    final: number;
    /** 이 자리에서 이어 치려면 필요한 최종속 */
    needFinal: number;
    /** 그러려면 필요한 룬속 */
    needFlat: number;
    /** 지금 룬속에서 모자란 만큼. 0 이면 충분 */
    short: number;
    enough: boolean;
    gotGauge: number;
    gotBuff: boolean;
}

export interface Result {
    leadFinal: number;
    /** 올림하기 전 총공속 — 화면 칩은 이 값을 반올림해 보여 준다(표는 올림) */
    leadExact: number;
    /** 선턴 몹이 처음 치는 틱 */
    tick: number;
    followers: FollowerResult[];
}

/** 최종속 — 정보창에 보이는 값이 아니라 전투에서 실제로 쓰이는 값 */
export function finalSpeed(base: number, flat: number, leaderPct: number, totemPct = TOTEM_PCT): number {
    return Math.ceil(base * (1 + (leaderPct + totemPct) / 100) + flat);
}

/** 선턴 몹이 처음 치는 틱 */
export function leadTick(final: number): number {
    const gain = ATB_PER_SPEED * final;
    return gain > 0 ? Math.floor(100 / gain) + 1 : Infinity;
}

/** 속업이 실제로 올려 주는 비율 (아티로 키운 값) */
export function buffRate(artiPct: number): number {
    return (SPEED_BUFF_PCT / 100) * (1 + (artiPct || 0) / 100);
}

/**
 * l번째 뒤 몹(몬2=1, 몬3=2)이 제 차례에 치려면 필요한 **최종속**.
 */
export function neededFinal(leadFinal: number, l: number, gaugePct: number, buffOn: boolean, artiPct: number): number {
    const d = leadTick(leadFinal);
    if (!Number.isFinite(d)) return Infinity;
    const o = buffOn ? buffRate(artiPct) : 0;
    const gainLead = ATB_PER_SPEED * leadFinal;
    const denom = ATB_PER_SPEED * (d + l * (1 + o));
    if (denom <= 0) return Infinity;
    return (gainLead * (d + l) - gaugePct) / denom;
}

/**
 * 판정 한 벌.
 *
 * `party[0]` 이 선턴, 그 뒤가 몬2·몬3 이다. 뒤 몹은 넣은 차례대로 쳐야 하므로, 뒤에서부터
 * 거슬러 올라가며 앞 몹이 뒷 몹보다 느리지 않게 필요 룬속을 끌어올린다.
 */
export function evaluate(party: Mon[], opt: Options): Result | null {
    if (party.length < 2) return null;
    const totem = opt.totemPct ?? TOTEM_PCT;
    const lead = party[0];
    const leadExact = lead.base * (1 + (opt.leaderPct + totem) / 100) + lead.flat + (lead.bonus || 0);
    const leadFinal = Math.ceil(leadExact);
    /*
     * 틱과 필요 속도는 **올림하기 전 값**으로 센다. 둥봇이 그렇게 하고, 실제로 그래야
     * 화면 숫자가 맞는다 — 403.43 으로 세면 4틱·필요 237, 404 로 세면 236 이 나온다.
     */
    const tick = leadTick(leadExact);

    const gotGauge = opt.mode === 'gauge' || opt.mode === 'both' ? opt.gaugePct : 0;
    const gotBuff = opt.mode === 'buff' || opt.mode === 'both';

    // 자리마다 필요한 최종속
    const need: number[] = party.slice(1).map((m, k) => neededFinal(leadExact, k + 1, gotGauge, gotBuff, m.artiPct || 0));

    // 앞 자리가 뒷 자리보다 느리면 차례가 뒤집힌다 — 뒤에서부터 끌어올린다
    for (let i = need.length - 2; i >= 0; i--) {
        if (need[i] < need[i + 1] + 1) need[i] = need[i + 1] + 1;
    }

    const followers: FollowerResult[] = party.slice(1).map((m, k) => {
        const mult = 1 + (opt.leaderPct + totem) / 100;
        // 패시브 속도(암헬하 등)는 룬속을 그만큼 덜어 준다
        const needFlat = Math.max(0, Math.ceil(need[k] - m.base * mult - (m.bonus || 0)));
        const fin = finalSpeed(m.base, m.flat + (m.bonus || 0), opt.leaderPct, totem);
        const short = Math.max(0, needFlat - m.flat);
        return {
            name: m.name,
            base: m.base,
            flat: m.flat,
            final: fin,
            needFinal: Math.ceil(need[k]),
            needFlat,
            short,
            enough: short === 0,
            gotGauge,
            gotBuff
        };
    });

    return { leadFinal, leadExact, tick, followers };
}
